import React, { useEffect, useMemo, useReducer, useState } from "react";
import { Tooltip } from "antd";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHand } from "@fortawesome/free-solid-svg-icons";
import IconDefinition from "./IconDefinition";
import { useLastLiftMessage, useDebounceMap } from "../socketMessageState/BankMessagesContext";
import useDirectionDebounce from "./useDirectionDebounce";
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface, BankFloorInterface } from "../../../interfaces/Bank.interface";
import { RuleMemory, RuleDefinition } from "../../../interfaces/IconDefinitions";
import { DeviceServiceContent } from "../../../flatbuffers/generated/service/device-service-content";
import { Service } from "../../../flatbuffers/generated/service/service";
import { usageToDoorIconFiltered, usageHasDoorData } from "../../../utils/bitmaskToIcon";
import { stringToUuidT } from "../../../utils/uuid";
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { Fault } from "../../../flatbuffers/generated/fault/fault";
import { FaultContent } from "../../../flatbuffers/generated/fault/fault-content";
import { FaultMessageT } from "../../../flatbuffers/generated/fault/fault-message";
import {
  updateMemoryWithMessage,
  findWinningRule,
  matchesExprOnMessage,
  conditionMatchesSocket,
} from "../../../utils/ruleEngine";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { Control } from "../../../flatbuffers/generated/control/control";
import {
  liveWithheldMarkAt,
  WITHHELD_MARK_TTL_MS,
} from "../../../flatbuffers/decoders/withheldMarks";

type SizeTier = "solo" | "pair" | "compact";

function faultDisplayName(f: FaultMessageT): string {
  const code = f.fault?.fault?.message ?? -1;
  if (f.faultType === FaultContent.ElevatorFaultMessage) {
    return Fault[code] ?? `Fault(${code})`;
  }
  return `Fault(${code})`;
}

interface FloorCellIconProps {
  floor: BankFloorInterface;
  lift: LiftInterface;
  bank: BankInterface;
  floorIndex: number;
  frontDoors: boolean;
  rearDoors: boolean;
  bankHasMixedDoors: boolean;
  variant?: "default" | "custom";
  adapterRules?: RuleDefinition[];
  iconSize?: number;
  badgeSize?: number;
  /**
   * Whether this lift actually serves this floor (from lift_floors). When false
   * the cell still occupies its slot in the shared shaft (so floor rows stay
   * aligned across lifts) but renders a dimmed "skip" marker instead of the car,
   * making it visually obvious where a lift does NOT stop while a sibling lift
   * in the same bank does. Defaults true for callers that don't pass per-lift
   * served data.
   */
  served?: boolean;
}

const SIZE_MAP: Record<SizeTier, { center: number; badge: number }> = {
  solo: { center: 20, badge: 20 },
  pair: { center: 16, badge: 10 },
  compact: { center: 14, badge: 6 },
};

const TIER_SCALE_MAP: Record<SizeTier, number> = { solo: 1, pair: 0.8, compact: 0.7 };
const DOT_SCALES: Record<number, number> = { 1: 1, 2: 0.85, 3: 0.7, 4: 0.6, 5: 0.52, 6: 0.45 };

function tierFromCount(count: number): SizeTier {
  if (count <= 1) return "solo";
  if (count === 2) return "pair";
  return "compact";
}

function FloorCellIcon({
  floor,
  lift,
  bank,
  floorIndex,
  frontDoors,
  rearDoors,
  bankHasMixedDoors,
  variant = "default",
  adapterRules = [],
  iconSize: propIconSize = 0,
  badgeSize: propBadgeSize = 0,
  served = true,
}: FloorCellIconProps) {
  const theme = useSelector((state: RootState) => state.home.theme);
  const doorUnknownAsOpening = useSelector(
    (state: RootState) => state.monitoring.doorUnknownAsOpening,
  );

  const liftUuid = useMemo(() => stringToUuidT(String(lift.id ?? "")), [lift.id]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = socketMessageLift.faults;
  const debounceMap = useDebounceMap();

  // VIP is derived — no useState needed
  const vipMode =
    socketMessageLift.serviceMode?.messageType === DeviceServiceContent.ElevatorServiceMessage &&
    socketMessageLift.serviceMode?.message?.message?.message === Service.VIP;

  const doorIcon = useMemo(
    () =>
      usageToDoorIconFiltered(
        socketMessageLift.usage,
        frontDoors,
        rearDoors,
        doorUnknownAsOpening ? { deriveUnknownAsOpening: true } : undefined,
        bankHasMixedDoors,
      ),
    [socketMessageLift.usage, doorUnknownAsOpening, frontDoors, rearDoors, bankHasMixedDoors],
  );

  const { directionIcon, lastDirectionIcon } = useDirectionDebounce(
    socketMessageLift.usage,
    debounceMap,
  );

  const activeFloors = useMemo(() => {
    const carFront = new Set(socketMessageLift.carCalls.front);
    const carRear = new Set(socketMessageLift.carCalls.rear);

    const hallUpFront = new Set(socketMessageLift.hallCalls.upAssigned.front);
    const hallUpRear = new Set(socketMessageLift.hallCalls.upAssigned.rear);
    const hallDownFront = new Set(socketMessageLift.hallCalls.downAssigned.front);
    const hallDownRear = new Set(socketMessageLift.hallCalls.downAssigned.rear);

    // Hall-call locks live on `up`/`down`, NOT on `upAssigned`/`downAssigned`.
    // `applyDeltaToState` (and `applyDeltaToStateLegacy`) route every
    // Control.{Up,Down}{,Rear}HallCall{Lock,Unlock} arm into
    // `hallCalls.{up,down}.{frontLock,rearLock}`, and nothing in the codebase
    // ever writes the `*Assigned` lock arrays. Reading the assigned slots here
    // therefore made the padlock badge unreachable for all four hall-call lock
    // types no matter what the controller reported — only car-call locks could
    // ever draw it. The plain (unlocked) call dots above genuinely are the
    // ASSIGNED arrays, which is what makes the mismatch easy to miss.
    const locks = new Set([
      ...socketMessageLift.carCalls.frontLock,
      ...socketMessageLift.carCalls.rearLock,
      ...socketMessageLift.hallCalls.up.frontLock,
      ...socketMessageLift.hallCalls.up.rearLock,
      ...socketMessageLift.hallCalls.down.frontLock,
      ...socketMessageLift.hallCalls.down.rearLock,
    ]);

    return { carFront, carRear, hallUpFront, hallUpRear, hallDownFront, hallDownRear, locks };
  }, [socketMessageLift]);

  // Rule engine state (custom variant only)
  const rules: RuleDefinition[] = useMemo(() => {
    if (variant !== "custom") return [];
    const hasConfiguredRules =
      adapterRules &&
      adapterRules.length > 0 &&
      adapterRules.some(
        (r) =>
          r.conditions &&
          r.conditions.length > 0 &&
          r.activeWhen?.kind === "group" &&
          r.activeWhen?.children?.length > 0,
      );
    return hasConfiguredRules ? adapterRules : [];
  }, [adapterRules, variant]);

  const [, setRuleMemory] = useState<RuleMemory>({});
  const [activeRule, setActiveRule] = useState<RuleDefinition | null>(null);

  // Derive ruleIcon from activeRule — no separate state needed
  const ruleIcon = activeRule?.icon ?? null;

  useEffect(() => {
    if (variant !== "custom") return;
    if (rules.length === 0) {
      setActiveRule(null);
      setRuleMemory({});
      return;
    }
    if (activeRule) {
      const shouldRemove = matchesExprOnMessage(
        activeRule.removeWhen,
        activeRule.conditions,
        socketMessageLift,
        conditionMatchesSocket,
      );
      if (shouldRemove) {
        setActiveRule(null);
        setRuleMemory({});
        return;
      }
      const replacer = rules.find((rule) => {
        if (rule.id === activeRule.id) return false;
        return matchesExprOnMessage(
          rule.activeWhen,
          rule.conditions,
          socketMessageLift,
          conditionMatchesSocket,
        );
      });
      if (replacer) {
        setActiveRule(replacer);
        setRuleMemory({});
      }
      return;
    }
    // Use functional updater to avoid ruleMemory in deps
    setRuleMemory((prev) => {
      const nextMemory = updateMemoryWithMessage(
        prev,
        rules,
        socketMessageLift,
        conditionMatchesSocket,
      );
      const winner = findWinningRule(rules, nextMemory);
      if (winner) {
        setActiveRule(winner);
        return {};
      }
      return nextMemory;
    });
  }, [socketMessageLift, variant, rules, activeRule]);

  const isAtPosition = floor.floorIndex === socketMessageLift.position;
  const hasCarCallFront = activeFloors.carFront.has(floor.floorIndex);
  const hasCarCallRear = activeFloors.carRear.has(floor.floorIndex);
  const hasHallUpFront = activeFloors.hallUpFront.has(floor.floorIndex);
  const hasHallUpRear = activeFloors.hallUpRear.has(floor.floorIndex);
  const hasHallDownFront = activeFloors.hallDownFront.has(floor.floorIndex);
  const hasHallDownRear = activeFloors.hallDownRear.has(floor.floorIndex);
  const hasLock = activeFloors.locks.has(floor.floorIndex);

  // A command VTS withheld on this floor: a DROP-policy channel-modifier gate
  // did not get its interlock's confirmation, so the press never reached the
  // controller. Aged against the wall clock rather than the next frame, because
  // an idle lift may send nothing further; the timer re-renders the cell when
  // the mark expires.
  const [, refreshWithheld] = useReducer((tick: number) => tick + 1, 0);
  const withheldHere = liveWithheldMarkAt(
    socketMessageLift.withheldCalls,
    floor.floorIndex,
    Date.now(),
  );
  useEffect(() => {
    if (withheldHere === null) return undefined;
    const remaining = withheldHere.atMs + WITHHELD_MARK_TTL_MS - Date.now();
    const timer = setTimeout(refreshWithheld, Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [withheldHere, refreshWithheld]);

  const isNegativePosition =
    socketMessageLift.position !== null && socketMessageLift.position < 0 && isAtPosition;

  const callDotCount = [
    hasHallUpFront,
    hasHallUpRear,
    hasHallDownFront,
    hasHallDownRear,
    hasCarCallFront,
    hasCarCallRear,
  ].filter(Boolean).length;
  const hasCenter = isAtPosition;

  const activeCount = [
    hasCarCallFront,
    hasCarCallRear,
    isAtPosition,
    hasHallUpFront,
    hasHallUpRear,
    hasHallDownFront,
    hasHallDownRear,
    hasLock,
  ].filter(Boolean).length;
  const tier = tierFromCount(activeCount);

  // Dynamic sizes from parent (ResizeObserver-driven) or fallback to static tiers
  const baseIconSize = propIconSize || SIZE_MAP[tier].center;
  const baseBadgeSize = propBadgeSize || SIZE_MAP[tier].badge;

  // Scale down for pair/compact tiers relative to the dynamic base
  const centerSize = Math.max(12, Math.round(baseIconSize * TIER_SCALE_MAP[tier]));
  const badgeSize = Math.max(6, Math.round(baseBadgeSize * TIER_SCALE_MAP[tier]));

  // Call dot size: larger when dots are the primary content, smaller when overlaying position
  const dotBaseSize = hasCenter ? baseBadgeSize : baseIconSize;
  const callDotSize = Math.max(6, Math.round(dotBaseSize * (DOT_SCALES[callDotCount] ?? 0.45)));

  const renderCenter = () => {
    if (isNegativePosition) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType={LiftType[LiftType.ELEVATOR]}
          icon="elWarning"
          iconSize={centerSize}
        />
      );
    }

    if (isAtPosition) {
      if (variant === "custom" && ruleIcon) {
        return (
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType={LiftType[LiftType.ELEVATOR]}
            icon={ruleIcon}
            iconSize={centerSize}
          />
        );
      }

      const hasDoorData = usageHasDoorData(socketMessageLift.usage);
      const displayDirection = directionIcon || lastDirectionIcon;
      const directionDimmed = !directionIcon && !!lastDirectionIcon;

      // Position known but no door data: show floor index + direction if available
      if (!hasDoorData) {
        return (
          <div
            data-testid="floor-cell-center"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "10px",
              fontWeight: "bold",
              opacity: 0.7,
            }}
          >
            <span data-testid="floor-cell-position-number">{floor.floorIndex}</span>
            {displayDirection && (
              <span
                data-testid="floor-cell-direction"
                style={{ opacity: directionDimmed ? 0.3 : 1, marginLeft: "1px" }}
              >
                <IconDefinition
                  isMap={false}
                  expanded={false}
                  liftType={LiftType[LiftType.ELEVATOR]}
                  icon={displayDirection}
                  iconSize={centerSize - 4}
                />
              </span>
            )}
          </div>
        );
      }

      return (
        <div
          data-testid="floor-cell-center"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType={LiftType[LiftType.ELEVATOR]}
            icon={doorIcon}
            iconSize={centerSize}
          />
          {displayDirection && (
            <span data-testid="floor-cell-direction" style={{ opacity: directionDimmed ? 0.3 : 1 }}>
              <IconDefinition
                isMap={false}
                expanded={false}
                liftType={LiftType[LiftType.ELEVATOR]}
                icon={displayDirection}
                iconSize={centerSize}
              />
            </span>
          )}
        </div>
      );
    }

    return null;
  };

  const renderCallDots = () => {
    if (callDotCount === 0) return null;

    // All 6 possible call dot slots in grid order:
    // Row 1 (front): upFrontAssigned(green), carFront(blue), downFrontAssigned(red)
    // Row 2 (rear):  upRearAssigned(green),  carRear(gold),  downRearAssigned(red)
    const allDots: Array<{ active: boolean; icon: string; testId: string }> = [
      { active: hasHallUpFront, icon: "elGoalGreen", testId: "floor-cell-hall-up-front" },
      { active: hasCarCallFront, icon: "elGoalBlue", testId: "floor-cell-car-call-front" },
      { active: hasHallDownFront, icon: "elGoalRed", testId: "floor-cell-hall-down-front" },
      { active: hasHallUpRear, icon: "elGoalGreen", testId: "floor-cell-hall-up-rear" },
      { active: hasCarCallRear, icon: "elGoalGold", testId: "floor-cell-car-call-rear" },
      { active: hasHallDownRear, icon: "elGoalRed", testId: "floor-cell-hall-down-rear" },
    ];

    // Single dot — render centered (or as small badge when position indicator is shown)
    if (callDotCount === 1) {
      const dot = allDots.find((d) => d.active) ?? allDots[0];
      return (
        <span
          data-testid={dot.testId}
          style={
            hasCenter
              ? { position: "absolute", left: 0, top: 0, zIndex: 2 }
              : { display: "flex", alignItems: "center", justifyContent: "center" }
          }
        >
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType={LiftType[LiftType.ELEVATOR]}
            icon={dot.icon}
            iconSize={callDotSize}
          />
        </span>
      );
    }

    // 2-6 dots — 3x2 grid (columns: up-assigned, car-call, down-assigned; rows: front, rear)
    const colSize = callDotSize + 2;
    const gridStyle: React.CSSProperties = hasCenter
      ? {
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          display: "grid",
          gridTemplateColumns: `repeat(3, ${colSize}px)`,
          gridTemplateRows: "1fr 1fr",
          alignItems: "center",
          justifyItems: "center",
          zIndex: 2,
        }
      : {
          display: "grid",
          gridTemplateColumns: `repeat(3, 1fr)`,
          gridTemplateRows: "1fr 1fr",
          gap: "1px",
          alignItems: "center",
          justifyItems: "center",
        };

    return (
      <div data-testid="floor-cell-call-dots" style={gridStyle}>
        {allDots.map((dot) => (
          <span key={dot.testId} data-testid={dot.active ? dot.testId : undefined}>
            {dot.active && (
              <IconDefinition
                isMap={false}
                expanded={false}
                liftType={LiftType[LiftType.ELEVATOR]}
                icon={dot.icon}
                iconSize={callDotSize}
              />
            )}
          </span>
        ))}
      </div>
    );
  };

  const renderLock = () => {
    if (!hasLock) return null;
    return (
      <span
        data-testid="floor-cell-lock"
        style={
          hasCenter || callDotCount > 0
            ? { position: "absolute", right: 0, bottom: 0, zIndex: 2 }
            : undefined
        }
      >
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType={LiftType[LiftType.ELEVATOR]}
          icon="elLocked"
          iconSize={badgeSize}
        />
      </span>
    );
  };

  const renderWithheld = () => {
    if (withheldHere === null) return null;
    const commandName = Control[withheldHere.control] ?? `Control ${withheldHere.control}`;
    const label = `${commandName} held back by an interlock: VTS did not send it`;
    return (
      <span
        data-testid="floor-cell-withheld"
        title={label}
        aria-label={label}
        className="generalStyles__withheld"
        style={
          hasCenter || callDotCount > 0 || hasLock
            ? { position: "absolute", left: 0, bottom: 0, zIndex: 2, lineHeight: 0 }
            : { lineHeight: 0 }
        }
      >
        <FontAwesomeIcon icon={faHand} style={{ fontSize: badgeSize }} />
      </span>
    );
  };

  const containerClass = [
    `monitoring__card__liftContainer__${theme}`,
    hasFaults ? "monitoringView__dangerElevator" : "",
    vipMode ? "monitoringView__vipElevator" : "",
    floorIndex === 0 && hasFaults ? "monitoringView__dangerElevator__top" : "",
    floorIndex === 0 && vipMode ? "monitoringView__vipElevator__top" : "",
    floorIndex === (bank.bankFloors ?? []).length - 1 && hasFaults
      ? "monitoringView__dangerElevator__bottom"
      : "",
    floorIndex === (bank.bankFloors ?? []).length - 1 && vipMode
      ? "monitoringView__vipElevator__bottom"
      : "",
    floorIndex === (bank.bankFloors ?? []).length - 1
      ? "monitoring__card__liftContainerBorderBottom"
      : "",
    floorIndex === 0 ? "monitoring__card__liftContainerBorderTop" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const serviceModeCode = socketMessageLift.serviceMode?.message?.message?.message;
  const serviceModeLabel =
    serviceModeCode !== undefined && serviceModeCode !== null && serviceModeCode !== Service.Auto
      ? Service[serviceModeCode]
      : null;

  const faultLines: string[] = [];
  if (hasFaults) {
    faultLines.push(...socketMessageLift.activeFaults.map((f) => faultDisplayName(f)));
  }
  if (serviceModeLabel) {
    faultLines.push(`Service: ${serviceModeLabel}`);
  }
  const cellTooltip = faultLines.join(", ");

  const cellContent = (
    <div className={containerClass} data-tier={tier} data-testid="floor-cell">
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {served ? (
          <>
            {renderCallDots()}
            {renderCenter()}
            {renderLock()}
            {renderWithheld()}
          </>
        ) : (
          // Floor the shaft shows (another lift serves it / it's in view scope)
          // but THIS lift does not stop at — render a dimmed dashed "skip" marker
          // so the gap is obvious instead of an empty/ambiguous cell.
          <span
            data-testid="floor-cell-gap"
            style={{
              display: "inline-block",
              width: "45%",
              borderTop: "2px dashed currentColor",
              opacity: 0.3,
            }}
          />
        )}
      </div>
    </div>
  );

  if (!served) {
    return (
      <Tooltip title={`${lift.name || "Lift"} does not serve this floor`} placement="right">
        {cellContent}
      </Tooltip>
    );
  }

  if (!cellTooltip) return cellContent;

  return (
    <Tooltip title={cellTooltip} placement="right">
      {cellContent}
    </Tooltip>
  );
}

export default React.memo(FloorCellIcon);
